// Pre-existing complexity (CTA decision tree, banner/popover JSX) — not part of
// the data-layer migration's intended scope.
// fallow-ignore-file complexity
import { useEffect, useMemo, useRef } from 'react';
import type { MouseEvent } from 'react';
import {
  Box,
  Paragraph,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  Popover,
  PopoverContent,
  List,
  ListItem,
  Link,
  Strong,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';

import { useQuery } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { getInboxLinkAsync } from '@/services/inbox-link';
import { isEmptyString } from '@/utils/string';
import { isSendingVerified, type Domain } from '@/services/db/domain/entity';
import { useDomainsOptional, type DomainsList } from '@/services/db/domain/queries';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { generatePath } from '@/services/router/generate-path';
import type { Route } from '@/services/router/routes';
import { AccountState, VerificationReviewState, VerificationState } from '@/services/auth/types';
import { authStore } from '@/services/auth/store';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import { useIsOnboardingChecklistVisible } from '@/features/onboarding/hooks';
import { OnboardingStepId, useHasRepliedToVerification } from '@/features/onboarding';
import { DeliverySettings, useSampleWorkspace, useSendTransactionalStep } from '@/features/workspaces';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useFeatureFlag, FeatureFlag } from '@/services/auth/feature-flags';
import { useAnalytics } from '@/services/analytics';
import * as styles from './index.css';

// Module-scope so the function reference is stable across renders —
// `useQuery`'s `select` memoization keys on it, and a fresh closure
// each render would defeat the structural-sharing optimization.
function selectVerifiedDomains(data: DomainsList): readonly Domain[] {
  // isSendingVerified: a barred domain would suppress the "Add a sending
  // domain" prompt while the workspace still cannot send.
  return data.domains.filter((d) => isSendingVerified(d));
}

const WORKSPACE_SETTINGS_EDIT_ROUTE = '/workspaces/:workspaceId/settings/edit' as const;
const WORKSPACE_HOME_ROUTE = '/workspaces/:workspaceId/home' as const;
const SENDING_DOMAINS_ROUTE = '/workspaces/:workspaceId/settings/actions/email/sending_domains' as const;

type WorkspaceSettingsEditRoute = typeof WORKSPACE_SETTINGS_EDIT_ROUTE;
type WorkspaceHomeRoute = typeof WORKSPACE_HOME_ROUTE;
type SendingDomainsRoute = typeof SENDING_DOMAINS_ROUTE;
type CtaDestinationRoute = Route | WorkspaceSettingsEditRoute | WorkspaceHomeRoute | SendingDomainsRoute;

interface CtaCopy<TRoute extends CtaDestinationRoute = CtaDestinationRoute> {
  link?: string;
  text?: string;
  route?: TRoute;
  query?: Record<string, string | boolean>;
}

const CTA_COPY = {
  deliverySettingsDisabledUnverified: {
    link: 'Change your message sending settings',
    text: 'and verify your account to send messages to your audience or yourself.',
    route: WORKSPACE_SETTINGS_EDIT_ROUTE,
  },
  sendingDomainNotSetUpUnverified: {
    link: 'Add a sending domain',
    text: 'and verify your account to send messages to your audience.',
    route: SENDING_DOMAINS_ROUTE,
  },
  fromAddressNotSetUpUnverified: {
    link: 'Add a From address',
    text: 'to your sending domain and verify your account to send messages to your audience.',
    route: SENDING_DOMAINS_ROUTE,
  },
  accountVerificationPending: {
    text: 'Your verification is currently being reviewed.',
  },
  accountVerificationRejected: {
    text: 'Your account was not approved.',
  },
  verifyAccount: {
    link: 'Verify your account',
    text: 'to send messages to your audience.',
    route: '/workspaces/:workspaceId/settings/actions/email/account_verification',
  },
  messageSendingSettings: {
    link: 'Change your message sending settings',
    text: 'to send messages to your audience or yourself.',
    route: WORKSPACE_SETTINGS_EDIT_ROUTE,
    query: {
      fragment: 'delivery-settings',
      enableMessageSending: true,
    },
  },
  sendingDomainNotSetUp: {
    link: 'Add a sending domain',
    text: 'to send messages to your audience.',
    route: SENDING_DOMAINS_ROUTE,
  },
  fromAddressNotSetUp: {
    link: 'Add a From address',
    text: 'to your sending domain to send messages to your audience.',
    route: SENDING_DOMAINS_ROUTE,
  },
  deliverySettingsSendToOne: {
    link: 'Change your message sending settings',
    text: 'to send messages to your audience.',
    route: WORKSPACE_SETTINGS_EDIT_ROUTE,
    query: {
      fragment: 'delivery-settings',
      enableMessageSending: true,
    },
  },
  builderCompleteSetup: {
    link: 'Complete setup',
    text: 'to send to your full audience.',
    route: WORKSPACE_HOME_ROUTE,
  },
} as const satisfies Record<string, CtaCopy>;

type ExtractCtaRoute<T> = T extends { route: infer TRoute extends CtaDestinationRoute } ? TRoute : never;
type DeliveryModeBannerCtaCopy = CtaCopy<ExtractCtaRoute<(typeof CTA_COPY)[keyof typeof CTA_COPY]>>;

export function DeliveryModeBanner() {
  const { track } = useAnalytics();
  const auth = useStore(authStore);
  const activeRoute = useStore(activeRouteStore);
  const router = useRouter();

  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? 0;

  const sendingDomainsHref = router.buildLocation({
    to: '/workspaces/$workspaceId/settings/actions/email/sending_domains',
    params: { workspaceId },
  }).href;

  const verificationStepHref = router.buildLocation({
    to: '/workspaces/$workspaceId/home',
    params: { workspaceId },
    search: { step: OnboardingStepId.AccountVerified },
  }).href;

  const buildCtaHref = (ctaCopy: DeliveryModeBannerCtaCopy) => {
    if (ctaCopy.route == null) {
      return '#';
    }

    if (ctaCopy.route === WORKSPACE_SETTINGS_EDIT_ROUTE) {
      return router.buildLocation({
        to: '/workspaces/$workspaceId/settings/edit',
        params: { workspaceId },
        search: ctaCopy.query ?? {},
      }).href;
    }

    if (ctaCopy.route === WORKSPACE_HOME_ROUTE) {
      return router.buildLocation({
        to: '/workspaces/$workspaceId/home',
        params: { workspaceId },
        search: ctaCopy.query ?? {},
      }).href;
    }

    if (ctaCopy.route === SENDING_DOMAINS_ROUTE) {
      return router.buildLocation({
        to: '/workspaces/$workspaceId/settings/actions/email/sending_domains',
        params: { workspaceId },
        search: ctaCopy.query ?? {},
      }).href;
    }

    return generatePath(ctaCopy.route, { workspaceId }, ctaCopy.query);
  };

  // Pass the current workspace id explicitly: the banner renders in the layout
  // chrome on non-numeric routes (e.g. /workspaces/last/...) where the URL-only
  // lookup would otherwise skip and leave setup copy wrong.
  const { data: environmentData } = useEnvironmentOptional(workspace?.id);

  const env = environmentData?.environment ?? null;

  const isFromAddressSetUp = useMemo(() => {
    const identityIds = new Set(env?.identity_ids ?? []);
    const filtered = (environmentData?.identities ?? []).filter((identity) => identityIds.has(identity.id));
    return filtered.length > 0;
  }, [env?.identity_ids, environmentData?.identities]);

  const { isSampleWorkspace } = useSampleWorkspace();
  const hasFeatureFlag = useFeatureFlag(FeatureFlag.TestDeliveryMode);
  const isAccountVerificationDisabled = useFeatureFlag(FeatureFlag.AccountVerification);

  const { data: verifiedDomains } = useDomainsOptional({ select: selectVerifiedDomains });
  const isSendingDomainSetUp = verifiedDomains != null && verifiedDomains.length > 0;

  const shouldShow = useMemo(() => {
    if (workspace == null) {
      return false;
    }

    // Don't show for sample workspace
    if (isSampleWorkspace(workspace.id)) {
      return false;
    }

    if (!hasFeatureFlag) {
      return false;
    }

    if (
      workspace.delivery_settings !== DeliverySettings.SendToOne &&
      workspace.delivery_settings !== DeliverySettings.Off
    ) {
      return false;
    }

    return true;
  }, [workspace, isSampleWorkspace, hasFeatureFlag]);

  const isTestDeliveryMode = workspace?.delivery_settings === DeliverySettings.SendToOne;
  const isDeliveryDisabled = workspace?.delivery_settings === DeliverySettings.Off;

  const isBuilder = isBuilderPlan(auth.account);
  const builderMustUpgrade = isBuilder && auth.account?.account_state === AccountState.Free;
  const { hasSentTransactional } = useSendTransactionalStep(isBuilder, workspace?.id ?? 0);

  const isAccountVerified = useMemo(() => {
    return auth.account?.verification_state === VerificationState.Approved;
  }, [auth?.account?.verification_state]);

  const isAccountVerificationPending = useMemo(() => {
    return auth.account?.verification_state === VerificationState.Pending;
  }, [auth?.account?.verification_state]);

  const hasRepliedToVerification = useHasRepliedToVerification();
  const verificationNeedsMoreInfo =
    auth.account?.verification_review_state === VerificationReviewState.NeedsMoreInfo && !hasRepliedToVerification;

  // The on-hold copy points at the verification step, which exists only inside
  // the onboarding checklist — so it ships only where home renders that checklist.
  const isOnboardingChecklistVisible = useIsOnboardingChecklistVisible(auth.account);

  const userEmail = auth.user?.email ?? '';
  const { data: inboxLink } = useQuery({
    queryKey: ['verification-review-inbox-link', userEmail],
    queryFn: () => getInboxLinkAsync(userEmail),
    enabled: verificationNeedsMoreInfo && !isEmptyString(userEmail),
    staleTime: Infinity,
  });

  const handleOpenVerificationReviewInbox = (event: MouseEvent) => {
    event.preventDefault();
    if (inboxLink == null) {
      return;
    }
    track('account', 'verificationReviewInboxLinkClicked', {
      'MX Provider': inboxLink.provider,
      'Email Domain': inboxLink.email.split('@')[1] ?? '',
    });
    window.open(inboxLink.url, '_blank', 'noopener,noreferrer');
  };
  const verificationRejected = auth.account?.verification_state === VerificationState.Rejected;

  // Decides which copy the test-mode banner shows for non-Builder workspaces. The banner reflects
  // the user's actual next step rather than always pointing at sending-domain setup. Unresolved
  // account verification (rejected, needs-more-info) outranks sending-domain setup: domain work
  // can't get messages delivered while verification is blocked.
  const nonBuilderTestModeVariant = useMemo<
    'rejected' | 'add-domain' | 'add-from-address' | 'needs-more-info' | 'move-out-of-test-mode' | null
  >(() => {
    if (!isTestDeliveryMode || isBuilder) {
      return null;
    }
    if (verificationRejected) {
      return 'rejected';
    }
    if (verificationNeedsMoreInfo) {
      return 'needs-more-info';
    }
    if (!isSendingDomainSetUp) {
      return 'add-domain';
    }
    if (!isFromAddressSetUp) {
      return 'add-from-address';
    }
    return 'move-out-of-test-mode';
  }, [
    isTestDeliveryMode,
    isBuilder,
    isSendingDomainSetUp,
    isFromAddressSetUp,
    verificationNeedsMoreInfo,
    verificationRejected,
  ]);

  const cta = useMemo<DeliveryModeBannerCtaCopy | null>(() => {
    if (isBuilder) {
      if (builderMustUpgrade) {
        return CTA_COPY.builderCompleteSetup;
      }
      if (isTestDeliveryMode) {
        return CTA_COPY.builderCompleteSetup;
      }
      // Builder paid + delivery off falls through to the regular path below.
    }

    if (!isAccountVerified) {
      // When the AccountVerification flag is on, the user can't manually verify —
      // verification status outranks domain-setup CTAs, since domain work can't
      // get messages delivered while the account is under review.
      if (isAccountVerificationDisabled) {
        if (isDeliveryDisabled) {
          return CTA_COPY.messageSendingSettings;
        }
        if (verificationRejected) {
          return CTA_COPY.accountVerificationRejected;
        }
        return CTA_COPY.accountVerificationPending;
      }

      if (verificationRejected) {
        return CTA_COPY.accountVerificationRejected;
      }
      if (verificationNeedsMoreInfo) {
        return CTA_COPY.accountVerificationPending;
      }

      if (isDeliveryDisabled) {
        return CTA_COPY.deliverySettingsDisabledUnverified;
      } else if (!isSendingDomainSetUp) {
        return CTA_COPY.sendingDomainNotSetUpUnverified;
      } else if (!isFromAddressSetUp) {
        return CTA_COPY.fromAddressNotSetUpUnverified;
      } else if (isAccountVerificationPending) {
        return CTA_COPY.accountVerificationPending;
      } else {
        return CTA_COPY.verifyAccount;
      }
    } else {
      if (isDeliveryDisabled) {
        return CTA_COPY.messageSendingSettings;
      } else if (!isSendingDomainSetUp) {
        return CTA_COPY.sendingDomainNotSetUp;
      } else if (!isFromAddressSetUp) {
        return CTA_COPY.fromAddressNotSetUp;
      } else if (isTestDeliveryMode) {
        return CTA_COPY.deliverySettingsSendToOne;
      }
    }
    return null;
  }, [
    isAccountVerified,
    isAccountVerificationDisabled,
    isAccountVerificationPending,
    isDeliveryDisabled,
    isSendingDomainSetUp,
    isFromAddressSetUp,
    isTestDeliveryMode,
    isBuilder,
    builderMustUpgrade,
    verificationRejected,
    verificationNeedsMoreInfo,
  ]);

  const trackPopoverOpenedRef = useRef<ReturnType<typeof window.setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (trackPopoverOpenedRef.current != null) {
        window.clearTimeout(trackPopoverOpenedRef.current);
      }
    };
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      trackPopoverOpenedRef.current = setTimeout(() => {
        track('test_delivery_mode', 'popover_opened', {
          'Route Name': activeRoute,
        });
      }, 750);
    } else {
      if (trackPopoverOpenedRef.current != null) {
        window.clearTimeout(trackPopoverOpenedRef.current);
        trackPopoverOpenedRef.current = null;
      }
    }
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <Popover
      shouldTriggerOnClick={false}
      shouldTriggerOnHover={true}
      shouldTriggerOnFocus={true}
      onOpenChange={handleOpenChange}
      initialFocus={-1}
    >
      {(popoverContext) => (
        <>
          <Box
            className={styles.deliveryModeBanner}
            ref={popoverContext.reference.setReference}
            {...popoverContext.reference.getReferenceProps()}
          >
            <Paragraph color="inherit">
              {isTestDeliveryMode && isBuilder && cta?.link != null && cta.route != null ? (
                <>
                  Emails are delivered to your test users only.{' '}
                  <Strong>
                    <Link href={buildCtaHref(cta)} variant="secondary" color="inherit">
                      {cta.link}
                    </Link>
                  </Strong>{' '}
                  {cta.text}
                </>
              ) : null}
              {nonBuilderTestModeVariant === 'add-domain' ? (
                <>
                  <Strong>Email sending</Strong> is in <Strong>test mode</Strong>.{' '}
                  <Link href={sendingDomainsHref} variant="secondary" color="inherit">
                    Add and verify a sending domain
                  </Link>{' '}
                  to reach your audience.
                </>
              ) : null}
              {nonBuilderTestModeVariant === 'add-from-address' ? (
                <>
                  <Strong>Email sending</Strong> is in <Strong>test mode</Strong>.{' '}
                  <Link href={sendingDomainsHref} variant="secondary" color="inherit">
                    Add a From address
                  </Link>{' '}
                  to your sending domain to reach your audience.
                </>
              ) : null}
              {nonBuilderTestModeVariant === 'rejected' ? (
                <>
                  Your account was not approved because the email domain and website information you provided at signup
                  does not align with our{' '}
                  <Link
                    href="https://customer.io/legal/acceptable-use-policy"
                    variant="secondary"
                    color="inherit"
                    isExternal={true}
                  >
                    Acceptable Use Policy
                  </Link>
                  . If you believe this is an error, please{' '}
                  <Link
                    href="#"
                    variant="secondary"
                    color="inherit"
                    onClick={(event: MouseEvent) => {
                      event.preventDefault();
                      window.__hydra_sidebar__?.openSupportForm?.('verification_rejected_banner');
                    }}
                  >
                    contact support
                  </Link>
                  .
                </>
              ) : null}
              {nonBuilderTestModeVariant === 'needs-more-info' ? (
                <>
                  We'd like to know a bit about how you'll use Customer.io.{' '}
                  {/* Straight to the step that holds the questions and the reply box.
                      Without a checklist there is nowhere in-product to answer, so
                      those accounts keep being pointed at the email instead. */}
                  {isOnboardingChecklistVisible ? (
                    <Link href={verificationStepHref} variant="secondary" color="inherit">
                      Tell us about your use case
                    </Link>
                  ) : (
                    <>
                      Check your inbox for an email with the subject{' '}
                      <Strong>"Action required to verify your Customer.io account"</Strong> for the next steps.
                      {inboxLink != null ? (
                        <>
                          {' '}
                          <Link
                            href={inboxLink.url}
                            variant="secondary"
                            color="inherit"
                            isExternal={true}
                            onClick={handleOpenVerificationReviewInbox}
                            data-test-verification-review-inbox-link
                          >
                            Open in {inboxLink.label}
                          </Link>
                        </>
                      ) : null}
                    </>
                  )}
                </>
              ) : null}
              {nonBuilderTestModeVariant === 'move-out-of-test-mode' ? (
                <>
                  <Strong>Email sending</Strong> is in <Strong>test mode</Strong>. Move out of test mode{' '}
                  <Link
                    href={
                      router.buildLocation({
                        to: '/workspaces/$workspaceId/settings/edit',
                        params: { workspaceId },
                        search: { fragment: 'delivery-settings', enableMessageSending: true },
                      }).href
                    }
                    variant="secondary"
                    color="inherit"
                  >
                    here
                  </Link>
                  .
                </>
              ) : null}
              {isDeliveryDisabled && builderMustUpgrade && cta?.link != null && cta.route != null ? (
                <>
                  Message sending is disabled.{' '}
                  <Link href={buildCtaHref(cta)} variant="secondary" color="inherit">
                    {cta.link}
                  </Link>{' '}
                  {cta.text}
                </>
              ) : null}
              {isDeliveryDisabled && !builderMustUpgrade ? (
                <>
                  Message sending is disabled. Enable sending in your{' '}
                  <Link
                    href={
                      router.buildLocation({
                        to: '/workspaces/$workspaceId/settings/edit',
                        params: { workspaceId },
                        search: { fragment: 'delivery-settings', enableMessageSending: true },
                      }).href
                    }
                    variant="secondary"
                    color="inherit"
                  >
                    workspace settings
                  </Link>
                  .
                </>
              ) : null}
            </Paragraph>
          </Box>
          <PopoverContent p="0" style={{ maxWidth: 460 }}>
            <Panel maxH="inherit" maxW="inherit" style={{ border: 'none' }}>
              <PanelHeader>
                <PanelTitle data-test-delivery-mode-popover-title>
                  {isTestDeliveryMode && isBuilder ? 'Delivers to test users only' : null}
                  {isTestDeliveryMode && !isBuilder ? 'Send emails to a test address' : null}
                  {isDeliveryDisabled ? "Don't send messages" : null}
                </PanelTitle>
              </PanelHeader>

              <PanelSection>
                {isTestDeliveryMode && isBuilder ? (
                  <Paragraph mb="100">Complete setup to send to your audience</Paragraph>
                ) : null}
                <List data-test-delivery-mode-popover-content styleType="none" gap="100">
                  {isTestDeliveryMode && isBuilder ? (
                    <>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: isSendingDomainSetUp ? 'success' : 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        Verify a{' '}
                        <Link href={sendingDomainsHref} variant="secondary">
                          sending domain
                        </Link>
                      </ListItem>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: hasSentTransactional ? 'success' : 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        Send a test email
                      </ListItem>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: !builderMustUpgrade ? 'success' : 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        {verificationRejected ? (
                          // A rejected account can't add funds; show plain text, not an actionable link.
                          'Add funds'
                        ) : (
                          <Link href="/settings/billing/your-plan?purchaseModal=account-funds" variant="secondary">
                            Add funds
                          </Link>
                        )}
                      </ListItem>
                    </>
                  ) : null}
                  {isTestDeliveryMode && !isBuilder ? (
                    <>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: 'caution' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        All emails are sent to <Strong>{workspace?.delivery_email}</Strong>
                      </ListItem>
                      <ListItem>
                        <Box
                          className={styles.circle({ variant: 'success' })}
                          centerBaseline="inline"
                          mr="050"
                          aria-hidden="true"
                        />
                        All other messages such as in-app, push, and SMS send as normal.
                      </ListItem>
                    </>
                  ) : null}
                  {isDeliveryDisabled ? (
                    <ListItem>
                      <Box
                        className={styles.circle({ variant: 'critical' })}
                        centerBaseline="inline"
                        mr="050"
                        aria-hidden="true"
                      />
                      All message sending is disabled
                    </ListItem>
                  ) : null}
                </List>
              </PanelSection>

              {cta != null && !(isTestDeliveryMode && isBuilder) ? (
                <PanelSection withBorder={true}>
                  {cta.link != null && cta.route != null ? (
                    <Link
                      data-test-delivery-mode-popover-cta
                      href={buildCtaHref(cta)}
                      variant="secondary"
                      onClick={(event: MouseEvent) => {
                        track('test_delivery_mode', 'popover_cta_clicked', {
                          'Route Name': activeRoute,
                          'Action Name': event.currentTarget.textContent?.trim(),
                        });

                        popoverContext.floatingContext.onOpenChange(false);
                      }}
                    >
                      {cta.link}
                    </Link>
                  ) : null}{' '}
                  {cta.text}
                </PanelSection>
              ) : null}
            </Panel>
          </PopoverContent>
        </>
      )}
    </Popover>
  );
}
